import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BorderOutlined,
  CalendarOutlined,
  DeleteOutlined,
  FileFilled,
  FolderFilled,
  FolderOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Input, Modal, Spin, Table, Tooltip, message } from "antd";
import { deleteSprZal, fetchSprZalList } from "./spr-zal-api";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import SprZalDrawer from "./spr-zal-drawer";
import SprZalFillModal from "./spr-zal-fill-modal";
import "./spr-zal.css";

const ROOT_UPPER = -1;

const STEP_LABELS = {
  5: "5 мин",
  10: "10 мин",
  15: "15 мин",
  30: "30 мин",
  60: "1 час"
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) return row[key];
  }
  return undefined;
};

const formatStep = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return STEP_LABELS[n] ?? `${n} мин`;
};

const formatTime = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU");
};

const formatNumber = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return String(n);
};

const toIsGroup = (value) => {
  if (value === true) return true;
  const n = Number(value);
  return Number.isFinite(n) && n === 1;
};

const normalizeRow = (row) => ({
  idZal: toNumber(pick(row, "idZal", "IdZal")),
  idUpper: toNumber(pick(row, "idUpper", "IdUpper")),
  isGroup: toIsGroup(pick(row, "isGroup", "IsGroup")),
  name: pick(row, "name", "Name") ?? "",
  planStepMin: pick(row, "planStepMin", "PlanStepMin"),
  planTimeBeg: pick(row, "planTimeBeg", "PlanTimeBeg"),
  planTimeEnd: pick(row, "planTimeEnd", "PlanTimeEnd"),
  edChild: pick(row, "edChild", "EdChild"),
  edParent: pick(row, "edParent", "EdParent"),
  maxDate: pick(row, "maxDate", "MaxDate"),
  sName: pick(row, "sName", "SName") ?? "",
  childCount: toNumber(pick(row, "childCount", "ChildCount")) ?? 0
});

/** Корни: ID_UPPER = -1 / 0 / null, либо родитель отсутствует в выборке. */
const resolveParentId = (idUpper, idSet) => {
  if (idUpper == null || idUpper === ROOT_UPPER || idUpper === 0) return ROOT_UPPER;
  if (!idSet.has(idUpper)) return ROOT_UPPER;
  return idUpper;
};

export const buildZalTree = (rows) => {
  const list = (Array.isArray(rows) ? rows : []).map(normalizeRow).filter((r) => r.idZal != null);
  const idSet = new Set(list.map((r) => r.idZal));
  const byParent = new Map();

  list.forEach((row) => {
    const parent = resolveParentId(row.idUpper, idSet);
    byParent.set(parent, [...(byParent.get(parent) || []), row]);
  });

  const build = (parentId) =>
    (byParent.get(parentId) || []).map((row) => {
      const children = build(row.idZal);
      return {
        key: String(row.idZal),
        idZal: row.idZal,
        isGroup: row.isGroup,
        name: row.name,
        planStepMin: row.planStepMin,
        planTimeBeg: row.planTimeBeg,
        planTimeEnd: row.planTimeEnd,
        edChild: row.edChild,
        edParent: row.edParent,
        maxDate: row.maxDate,
        sName: row.sName,
        childCount: row.childCount,
        ...(children.length ? { children } : {})
      };
    });

  return build(ROOT_UPPER);
};

const flattenParentOptions = (nodes, depth = 0, acc = []) => {
  (nodes || []).forEach((node) => {
    const pad = depth > 0 ? `${"\u00A0".repeat(depth * 3)}` : "";
    acc.push({ value: node.idZal, label: `${pad}${node.name || `№${node.idZal}`}` });
    if (node.children?.length) flattenParentOptions(node.children, depth + 1, acc);
  });
  return acc;
};

const findInTree = (nodes, idZal) => {
  for (const node of nodes || []) {
    if (node.idZal === idZal) return node;
    const nested = findInTree(node.children, idZal);
    if (nested) return nested;
  }
  return null;
};

/** Оставляет совпадения по имени и всех предков; раскрывает путь к найденным. */
const filterZalTree = (nodes, query) => {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return { tree: nodes || [], expandKeys: null };

  const expandKeys = [];
  const walk = (list) => {
    const out = [];
    (list || []).forEach((node) => {
      const childResult = walk(node.children || []);
      const nameMatch = String(node.name || "")
        .toLowerCase()
        .includes(q);
      if (nameMatch || childResult.length) {
        const next = { ...node };
        if (childResult.length) {
          next.children = childResult;
          expandKeys.push(node.key);
        } else {
          delete next.children;
        }
        out.push(next);
      }
    });
    return out;
  };

  return { tree: walk(nodes), expandKeys };
};

const collectExpandableKeys = (nodes, acc = []) => {
  (nodes || []).forEach((node) => {
    if (node.children?.length) {
      acc.push(node.key);
      collectExpandableKeys(node.children, acc);
    }
  });
  return acc;
};

const SprZal = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;
  const newIdUpper = toNumber(searchParams.get("upper"));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [contextIdZal, setContextIdZal] = useState(null);
  const [fillOpen, setFillOpen] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const expandedInitialized = useRef(false);
  const requestIdRef = useRef(0);

  const loadItems = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    expandedInitialized.current = false;
    fetchSprZalList()
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось загрузить кабинеты");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const treeData = useMemo(() => buildZalTree(rows), [rows]);
  const parentOptions = useMemo(() => flattenParentOptions(treeData), [treeData]);

  const { tree: filteredTreeData, expandKeys: filterExpandKeys } = useMemo(
    () => filterZalTree(treeData, nameFilter),
    [treeData, nameFilter]
  );

  useEffect(() => {
    if (nameFilter.trim()) {
      setExpandedRowKeys(filterExpandKeys || collectExpandableKeys(filteredTreeData));
      return;
    }
    if (expandedInitialized.current || !treeData.length) return;
    expandedInitialized.current = true;
    setExpandedRowKeys(treeData.filter((node) => node.children?.length).map((node) => node.key));
  }, [treeData, nameFilter, filteredTreeData, filterExpandKeys]);

  useEffect(() => {
    if (editId == null || loading || !rows.length) return;
    if (!findInTree(treeData, editId)) navigate("/spr/zal", { replace: true });
  }, [editId, treeData, loading, rows.length, navigate]);

  useEffect(() => {
    if (editId != null) setContextIdZal(editId);
  }, [editId]);

  const openEdit = useCallback(
    (record) => {
      if (record?.idZal == null) return;
      setContextIdZal(record.idZal);
      navigate(`/spr/zal/${record.idZal}`);
    },
    [navigate]
  );

  const openCreate = useCallback(() => {
    navigate("/spr/zal/new");
  }, [navigate]);

  const openCreateInGroup = useCallback(
    (record) => {
      if (record?.idZal == null) return;
      navigate(`/spr/zal/new?upper=${record.idZal}`);
    },
    [navigate]
  );

  const openFill = useCallback((record) => {
    if (record?.idZal == null) return;
    setContextIdZal(record.idZal);
    setFillOpen(true);
  }, []);

  const onDelete = useCallback(
    (record) => {
      if (record?.idZal == null) return;
      if (record.children?.length || record.childCount > 0) return;

      Modal.confirm({
        title: "Удалить?",
        content: record.name || `№${record.idZal}`,
        okText: "Удалить",
        cancelText: "Отмена",
        okButtonProps: { danger: true },
        onOk: () =>
          deleteSprZal(record.idZal)
            .then(() => {
              message.success("Удалено");
              if (editId === record.idZal) navigate("/spr/zal");
              loadItems();
            })
            .catch((e) => {
              message.error(e.message || "Не удалось удалить");
              return Promise.reject(e);
            })
      });
    },
    [editId, loadItems, navigate]
  );

  const closeDrawer = useCallback(() => {
    navigate("/spr/zal");
  }, [navigate]);

  const onSaved = useCallback(() => {
    loadItems();
    closeDrawer();
  }, [loadItems, closeDrawer]);

  const columns = useMemo(
    () => [
      {
        title: "Наименование",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
        className: "spr-zal__name-cell",
        render: (name, record) => {
          const isGroup = Boolean(record.isGroup);
          const isEditing = editId != null && record.idZal === editId;
          const hasChildren = Boolean(record.children?.length) || (record.childCount ?? 0) > 0;
          const TypeIcon = isGroup
            ? isEditing
              ? FolderFilled
              : FolderOutlined
            : isEditing
              ? FileFilled
              : BorderOutlined;
          return (
            <span className={`spr-zal__name${isEditing ? " spr-zal__name--editing" : ""}`}>
              <TypeIcon
                className={[
                  "spr-zal__type-icon",
                  isGroup ? "spr-zal__type-icon--group" : "",
                  isEditing ? "spr-zal__type-icon--editing" : ""
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-hidden
              />
              <span className="spr-zal__name-text">{name}</span>
              <span className="spr-zal__actions" onClick={(e) => e.stopPropagation()}>
                <Tooltip title="Заполнить">
                  <Button
                    type="text"
                    size="small"
                    className="spr-zal__row-action"
                    icon={<CalendarOutlined />}
                    aria-label="Заполнить"
                    onClick={() => openFill(record)}
                  />
                </Tooltip>
                {isGroup ? (
                  <Tooltip title="Добавить в группу">
                    <Button
                      type="text"
                      size="small"
                      className="spr-zal__row-action"
                      icon={<PlusOutlined />}
                      aria-label="Добавить в группу"
                      onClick={() => openCreateInGroup(record)}
                    />
                  </Tooltip>
                ) : null}
                {!hasChildren ? (
                  <Tooltip title="Удалить">
                    <Button
                      type="text"
                      size="small"
                      danger
                      className="spr-zal__row-action"
                      icon={<DeleteOutlined />}
                      aria-label="Удалить"
                      onClick={() => onDelete(record)}
                    />
                  </Tooltip>
                ) : null}
              </span>
            </span>
          );
        }
      },
      {
        title: "Шаг",
        dataIndex: "planStepMin",
        key: "planStepMin",
        width: 100,
        render: formatStep
      },
      {
        title: "Начало",
        dataIndex: "planTimeBeg",
        key: "planTimeBeg",
        width: 90,
        render: formatTime
      },
      {
        title: "Конец",
        dataIndex: "planTimeEnd",
        key: "planTimeEnd",
        width: 90,
        render: formatTime
      },
      {
        title: "Ед. дети",
        dataIndex: "edChild",
        key: "edChild",
        width: 100,
        align: "right",
        render: formatNumber
      },
      {
        title: "Ед. взрослые",
        dataIndex: "edParent",
        key: "edParent",
        width: 120,
        align: "right",
        render: formatNumber
      },
      {
        title: "График до",
        dataIndex: "maxDate",
        key: "maxDate",
        width: 110,
        render: formatDate
      },
      {
        title: "Склад",
        dataIndex: "sName",
        key: "sName",
        width: 180,
        ellipsis: true
      }
    ],
    [editId, openFill, openCreateInGroup, onDelete]
  );

  return (
    <div className="spr-zal">
      {error ? <Alert className="spr-zal__error" type="error" showIcon message={error} /> : null}
      <div className="spr-zal__body">
        <div className="spr-zal__list">
          <div className="spr-zal__toolbar">
            <div className="spr-zal__extra-actions">
              <Tooltip title="Создать">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  aria-label="Создать"
                  onClick={openCreate}
                />
              </Tooltip>
              <ReportButton
                vid={REPORT_VID.CABINETS}
                id={contextIdZal}
                compact
                disabled={contextIdZal == null}
              />
            </div>
            <Input
              allowClear
              variant="borderless"
              className="spr-zal__filter"
              placeholder="Фильтр по имени кабинета"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <div className="spr-zal__list-body">
            {loading && !rows.length ? (
              <div className="spr-zal__center">
                <Spin tip="Загрузка кабинетов..." />
              </div>
            ) : (
              <Table
                className="spr-zal__table"
                size="small"
                rowKey="key"
                columns={columns}
                dataSource={filteredTreeData}
                loading={loading}
                pagination={false}
                expandable={{
                  expandedRowKeys,
                  onExpandedRowsChange: setExpandedRowKeys
                }}
                onRow={(record) => ({
                  onClick: () => openEdit(record)
                })}
                rowClassName={(record) =>
                  [
                    "spr-zal__row",
                    editId != null && record.idZal === editId ? "spr-zal__row--editing" : ""
                  ]
                    .filter(Boolean)
                    .join(" ")
                }
                scroll={{ x: 960 }}
                locale={{
                  emptyText: nameFilter.trim()
                    ? "Нет кабинетов по фильтру"
                    : rows.length
                      ? "Не удалось построить дерево"
                      : "Нет кабинетов"
                }}
              />
            )}
          </div>
        </div>
      </div>
      <SprZalDrawer
        open={drawerOpen}
        idZal={editId}
        isNew={isNew}
        idUpper={newIdUpper}
        parentOptions={parentOptions}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
      <SprZalFillModal
        open={fillOpen}
        contextIdZal={contextIdZal}
        treeData={treeData}
        onClose={() => setFillOpen(false)}
        onDone={loadItems}
      />
    </div>
  );
};

export default SprZal;
